import { createReducer } from '@reduxjs/toolkit';
import {
  CREATE_FOLDER,
  DELETE_DOCUMENT,
  DELETE_FOLDER,
  GET_DOCUMENT,
  GET_DOCUMENTS,
  GET_FOLDER_DOCUMENTS,
  GET_FOLDER_OPTIONS,
  MOVE_DOCUMENT,
  UPDATE_DOCUMENT,
  UPDATE_FOLDER,
  UPLOAD_DOCUMENT,
  UPLOAD_DOCUMENT_PROGRESS
} from '../constants';
import { requestFail, requestPending, requestSuccess } from '../../utils/status';

const unwrapListBody = (payload) => {
  if (!payload) {
    return null;
  }
  // apiCall wraps paginated responses as { data: res.data, pagination }
  if (payload?.data && (payload.data.folders || payload.data.data || payload.data.folder)) {
    return payload.data;
  }
  return payload;
};

const parseDocumentsPayload = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  const body = unwrapListBody(payload);

  if (Array.isArray(body?.data)) {
    return body.data;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  return [];
};

const parseFoldersPayload = (payload) => {
  const body = unwrapListBody(payload);
  if (Array.isArray(body?.folders)) {
    return body.folders;
  }
  return [];
};

const parseCurrentFolder = (payload) => {
  const body = unwrapListBody(payload);
  return body?.folder || null;
};

const parseDocumentsPagination = (payload) => {
  const headerPagination = payload?.pagination;
  if (headerPagination?.['pagination-total']) {
    const perPage = Number(headerPagination['pagination-per-page']) || 20;
    const total = Number(headerPagination['pagination-total']) || 0;

    return {
      currentPage: Number(headerPagination['pagination-current-page']) || 1,
      perPage,
      total,
      totalPages: perPage > 0 ? Math.max(1, Math.ceil(total / perPage)) : 1
    };
  }

  const body = unwrapListBody(payload);
  const bodyPagination = body?.pagination || payload?.data?.pagination;
  if (bodyPagination) {
    return {
      currentPage: bodyPagination.currentPage || 1,
      perPage: bodyPagination.pageSize || 20,
      total: bodyPagination.totalResults || 0,
      totalPages: bodyPagination.totalPages || 1
    };
  }

  return {
    currentPage: 1,
    perPage: 20,
    total: 0,
    totalPages: 1
  };
};

const initialState = {
  documents: [],
  folders: [],
  folderOptions: [],
  currentFolder: null,
  document: null,
  pagination: {
    currentPage: 1,
    perPage: 20,
    total: 0,
    totalPages: 1
  },
  lastListParams: null,
  lastListMode: 'root',
  lastFolderId: null,
  getDocumentsStatus: '',
  getDocumentStatus: '',
  getFolderDocumentsStatus: '',
  getFolderOptionsStatus: '',
  uploadDocumentStatus: '',
  uploadProgress: null,
  uploadStalled: false,
  updateDocumentStatus: '',
  deleteDocumentStatus: '',
  createFolderStatus: '',
  updateFolderStatus: '',
  deleteFolderStatus: '',
  moveDocumentStatus: ''
};

export default createReducer(initialState, {
  [GET_DOCUMENTS]: (state, { payload }) => ({
    ...state,
    lastListParams: payload?.params ?? state.lastListParams,
    lastListMode: 'root',
    lastFolderId: null
  }),

  [requestSuccess(GET_DOCUMENTS)]: (state, { payload }) => ({
    ...state,
    getDocumentsStatus: requestSuccess(GET_DOCUMENTS),
    documents: parseDocumentsPayload(payload),
    folders: parseFoldersPayload(payload),
    currentFolder: null,
    pagination: parseDocumentsPagination(payload)
  }),

  [requestPending(GET_DOCUMENTS)]: (state) => ({
    ...state,
    getDocumentsStatus: requestPending(GET_DOCUMENTS)
  }),

  [requestFail(GET_DOCUMENTS)]: (state) => ({
    ...state,
    getDocumentsStatus: requestFail(GET_DOCUMENTS),
    documents: [],
    folders: [],
    currentFolder: null,
    pagination: initialState.pagination
  }),

  [GET_FOLDER_DOCUMENTS]: (state, { payload }) => ({
    ...state,
    lastListParams: payload?.params ?? state.lastListParams,
    lastListMode: 'folder',
    lastFolderId: payload?.id ?? state.lastFolderId
  }),

  [requestSuccess(GET_FOLDER_DOCUMENTS)]: (state, { payload }) => ({
    ...state,
    getFolderDocumentsStatus: requestSuccess(GET_FOLDER_DOCUMENTS),
    documents: parseDocumentsPayload(payload),
    folders: [],
    currentFolder: parseCurrentFolder(payload),
    pagination: parseDocumentsPagination(payload)
  }),

  [requestPending(GET_FOLDER_DOCUMENTS)]: (state) => ({
    ...state,
    getFolderDocumentsStatus: requestPending(GET_FOLDER_DOCUMENTS)
  }),

  [requestFail(GET_FOLDER_DOCUMENTS)]: (state) => ({
    ...state,
    getFolderDocumentsStatus: requestFail(GET_FOLDER_DOCUMENTS),
    documents: [],
    folders: [],
    pagination: initialState.pagination
  }),

  [requestSuccess(GET_FOLDER_OPTIONS)]: (state, { payload }) => ({
    ...state,
    getFolderOptionsStatus: requestSuccess(GET_FOLDER_OPTIONS),
    folderOptions: Array.isArray(payload) ? payload : []
  }),

  [requestPending(GET_FOLDER_OPTIONS)]: (state) => ({
    ...state,
    getFolderOptionsStatus: requestPending(GET_FOLDER_OPTIONS)
  }),

  [requestFail(GET_FOLDER_OPTIONS)]: (state) => ({
    ...state,
    getFolderOptionsStatus: requestFail(GET_FOLDER_OPTIONS),
    folderOptions: []
  }),

  [requestSuccess(GET_DOCUMENT)]: (state, { payload }) => ({
    ...state,
    getDocumentStatus: requestSuccess(GET_DOCUMENT),
    document: payload?.data ?? payload ?? null
  }),

  [requestPending(GET_DOCUMENT)]: (state) => ({
    ...state,
    getDocumentStatus: requestPending(GET_DOCUMENT)
  }),

  [requestFail(GET_DOCUMENT)]: (state) => ({
    ...state,
    getDocumentStatus: requestFail(GET_DOCUMENT),
    document: null
  }),

  [requestSuccess(UPLOAD_DOCUMENT)]: (state, { payload }) => ({
    ...state,
    uploadDocumentStatus: requestSuccess(UPLOAD_DOCUMENT),
    uploadProgress: null,
    uploadStalled: false,
    document: payload != null ? payload?.data ?? payload : state.document
  }),

  [requestPending(UPLOAD_DOCUMENT)]: (state) => ({
    ...state,
    uploadDocumentStatus: requestPending(UPLOAD_DOCUMENT),
    uploadProgress: 0,
    uploadStalled: false
  }),

  [UPLOAD_DOCUMENT_PROGRESS]: (state, { payload }) => ({
    ...state,
    uploadProgress:
      typeof payload?.progress === 'number' ? payload.progress : state.uploadProgress,
    uploadStalled: Boolean(payload?.stalled)
  }),

  [requestFail(UPLOAD_DOCUMENT)]: (state) => ({
    ...state,
    uploadDocumentStatus: requestFail(UPLOAD_DOCUMENT),
    uploadProgress: null,
    uploadStalled: false
  }),

  [requestSuccess(UPDATE_DOCUMENT)]: (state) => ({
    ...state,
    updateDocumentStatus: requestSuccess(UPDATE_DOCUMENT)
  }),

  [requestPending(UPDATE_DOCUMENT)]: (state) => ({
    ...state,
    updateDocumentStatus: requestPending(UPDATE_DOCUMENT)
  }),

  [requestFail(UPDATE_DOCUMENT)]: (state) => ({
    ...state,
    updateDocumentStatus: requestFail(UPDATE_DOCUMENT)
  }),

  [requestSuccess(DELETE_DOCUMENT)]: (state) => ({
    ...state,
    deleteDocumentStatus: requestSuccess(DELETE_DOCUMENT)
  }),

  [requestPending(DELETE_DOCUMENT)]: (state) => ({
    ...state,
    deleteDocumentStatus: requestPending(DELETE_DOCUMENT)
  }),

  [requestFail(DELETE_DOCUMENT)]: (state) => ({
    ...state,
    deleteDocumentStatus: requestFail(DELETE_DOCUMENT)
  }),

  [requestSuccess(CREATE_FOLDER)]: (state) => ({
    ...state,
    createFolderStatus: requestSuccess(CREATE_FOLDER)
  }),

  [requestPending(CREATE_FOLDER)]: (state) => ({
    ...state,
    createFolderStatus: requestPending(CREATE_FOLDER)
  }),

  [requestFail(CREATE_FOLDER)]: (state) => ({
    ...state,
    createFolderStatus: requestFail(CREATE_FOLDER)
  }),

  [requestSuccess(UPDATE_FOLDER)]: (state) => ({
    ...state,
    updateFolderStatus: requestSuccess(UPDATE_FOLDER)
  }),

  [requestPending(UPDATE_FOLDER)]: (state) => ({
    ...state,
    updateFolderStatus: requestPending(UPDATE_FOLDER)
  }),

  [requestFail(UPDATE_FOLDER)]: (state) => ({
    ...state,
    updateFolderStatus: requestFail(UPDATE_FOLDER)
  }),

  [requestSuccess(DELETE_FOLDER)]: (state) => ({
    ...state,
    deleteFolderStatus: requestSuccess(DELETE_FOLDER)
  }),

  [requestPending(DELETE_FOLDER)]: (state) => ({
    ...state,
    deleteFolderStatus: requestPending(DELETE_FOLDER)
  }),

  [requestFail(DELETE_FOLDER)]: (state) => ({
    ...state,
    deleteFolderStatus: requestFail(DELETE_FOLDER)
  }),

  [requestSuccess(MOVE_DOCUMENT)]: (state) => ({
    ...state,
    moveDocumentStatus: requestSuccess(MOVE_DOCUMENT)
  }),

  [requestPending(MOVE_DOCUMENT)]: (state) => ({
    ...state,
    moveDocumentStatus: requestPending(MOVE_DOCUMENT)
  }),

  [requestFail(MOVE_DOCUMENT)]: (state) => ({
    ...state,
    moveDocumentStatus: requestFail(MOVE_DOCUMENT)
  })
});
